/**
 * All SiteLocation CRUD actions
 *
 * Actions are payloads of information that send data from the application
 * (i.e. Yote server) to the store. They are the _only_ source of information
 * for the store.
 *
 * NOTE: In Yote, we try to keep actions and reducers dealing with CRUD payloads
 * in terms of 'item' or 'items'. This keeps the action payloads consistent and
 * aides various scoping issues with list management in the reducers.
 */

// import api utility
import callAPI from '../../global/utils/api'

const shouldFetchSingle = (state, id) => {
  /**
   * This is helper method to determine whether we should fetch a new single
   * user object from the server, or if a valid one already exists in the store
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetch single");
  const { byId, selected } = state.siteLocation;
  if(selected.id !== id) {
    // the "selected" id changed, so we _should_ fetch
    // console.log("Y shouldFetch - true: id changed");
    return true;
  } else if(selected.isFetching) {
    // "selected" is already fetching, don't do anything
    // console.log("Y shouldFetch - false: isFetching");
    return false;
  } else if(!byId[id] && !selected.error) {
    // the id is not in the map, fetch from server
    // however, if the api returned an error, then it SHOULDN'T be in the map
    // so re-fetching it will result in an infinite loop
    // console.log("Y shouldFetch - true: not in map");
    return true;
  } else if(new Date().getTime() - selected.lastUpdated > (1000 * 60 * 5)) {
    // it's been longer than 5 minutes since the last fetch, get a new one
    // console.log("Y shouldFetch - true: older than 5 minutes");
    // also, don't automatically invalidate on server error. if server throws an error,
    // that won't change on subsequent requests and we will have an infinite loop
    return true;
  } else {
    // if "selected" is invalidated, fetch a new one, otherwise don't
    // console.log("Y shouldFetch - " + selected.didInvalidate + ": didInvalidate");
    return selected.didInvalidate;
  }
}

export const INVALIDATE_SELECTED_SITE_LOCATION = "INVALIDATE_SELECTED_SITE_LOCATION"
export function invalidateSelected() {
  return {
    type: INVALIDATE_SELECTED_SITE_LOCATION
  }
}

export const fetchSingleIfNeeded = (id) => (dispatch, getState) => {
  if (shouldFetchSingle(getState(), id)) {
    return dispatch(fetchSingleSiteLocationById(id))
  } else {
    return dispatch(returnSingleSiteLocationPromise(id)); // return promise that contains siteLocation
  }
}

export const returnSingleSiteLocationPromise = (id) => (dispatch, getState) => {
  /**
   * This returns the object from the map so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. this is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */
  return new Promise((resolve, reject) => {
    resolve({
      type: "RETURN_SINGLE_SITE_LOCATION_WITHOUT_FETCHING"
      , id: id
      , item: getState().siteLocation.byId[id]
      , success: true
    })
  });
}

export const REQUEST_SINGLE_SITE_LOCATION = "REQUEST_SINGLE_SITE_LOCATION";
function requestSingleSiteLocation(id) {
  return {
    type: REQUEST_SINGLE_SITE_LOCATION
    , id
  }
}

export const RECEIVE_SINGLE_SITE_LOCATION = "RECEIVE_SINGLE_SITE_LOCATION";
function receiveSingleSiteLocation(json) {
  return {
    type: RECEIVE_SINGLE_SITE_LOCATION
    , id: json.siteLocation ? json.siteLocation._id : null
    , item: json.siteLocation
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function fetchSingleSiteLocationById(siteLocationId) {
  return dispatch => {
    dispatch(requestSingleSiteLocation(siteLocationId))
    return callAPI(`/api/site-locations/${siteLocationId}`)
      .then(json => dispatch(receiveSingleSiteLocation(json)))
  }
}

export const ADD_SINGLE_SITE_LOCATION_TO_MAP = "ADD_SINGLE_SITE_LOCATION_TO_MAP";
export function addSingleSiteLocationToMap(item) {
  return {
    type: ADD_SINGLE_SITE_LOCATION_TO_MAP
    , item
  }
}

export const REQUEST_CREATE_SITE_LOCATION = "REQUEST_CREATE_SITE_LOCATION";
function requestCreateSiteLocation(siteLocation) {
  return {
    type: REQUEST_CREATE_SITE_LOCATION
    , siteLocation
  }
}

export const RECEIVE_CREATE_SITE_LOCATION = "RECEIVE_CREATE_SITE_LOCATION";
function receiveCreateSiteLocation(json) {
  return {
    type: RECEIVE_CREATE_SITE_LOCATION
    , id: json.siteLocation ? json.siteLocation._id : null
    , item: json.siteLocation
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendCreateSiteLocation(data) {
  return dispatch => {
    dispatch(requestCreateSiteLocation(data))
    return callAPI('/api/site-locations', 'POST', data)
      .then(json => dispatch(receiveCreateSiteLocation(json)))
  }
}

export const REQUEST_UPDATE_SITE_LOCATION = "REQUEST_UPDATE_SITE_LOCATION";
function requestUpdateSiteLocation(siteLocation) {
  return {
    id: siteLocation ? siteLocation._id : null
    , siteLocation
    , type: REQUEST_UPDATE_SITE_LOCATION
  }
}

export const RECEIVE_UPDATE_SITE_LOCATION = "RECEIVE_UPDATE_SITE_LOCATION";
function receiveUpdateSiteLocation(json) {
  return {
    type: RECEIVE_UPDATE_SITE_LOCATION
    , id: json.siteLocation ? json.siteLocation._id : null
    , item: json.siteLocation
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export function sendUpdateSiteLocation(data) {
  return dispatch => {
    dispatch(requestUpdateSiteLocation(data))
    return callAPI(`/api/site-locations/${data._id}`, 'PUT', data)
      .then(json => dispatch(receiveUpdateSiteLocation(json)))
  }
}

export const REQUEST_DELETE_SITE_LOCATION = "REQUEST_DELETE_SITE_LOCATION";
function requestDeleteSiteLocation(id) {
  return {
    type: REQUEST_DELETE_SITE_LOCATION
    , id
  }
}

export const RECEIVE_DELETE_SITE_LOCATION = "RECEIVE_DELETE_SITE_LOCATION";
function receiveDeleteSiteLocation(id, json) {
  return {
    id
    , error: json.message
    , receivedAt: Date.now()
    , success: json.success
    , type: RECEIVE_DELETE_SITE_LOCATION
  }
}

export function sendDelete(id) {
  return dispatch => {
    dispatch(requestDeleteSiteLocation(id))
    return callAPI(`/api/site-locations/${id}`, 'DELETE')
      .then(json => dispatch(receiveDeleteSiteLocation(id, json)))
  }
}


/**
 * SITE_LOCATION LIST ACTIONS
 */

const findListFromArgs = (state, listArgs) => {
  /**
   * Helper method to find appropriate list from listArgs.
   *
   * Because we nest siteLocationLists to arbitrary locations/depths,
   * finding the correct list becomes a little bit harder
   */
  // let list = Object.assign({}, state.siteLocation.lists, {});
  let list = { ...state.siteLocation.lists }
  for(let i = 0; i < listArgs.length; i++) {
    list = list[listArgs[i]];
    if(!list) {
      return false;
    }
  }
  return list;
}

const shouldFetchList = (state, listArgs) => {
  /**
   * Helper method to determine whether to fetch the list or not from arbitrary
   * listArgs
   *
   * NOTE: Uncomment console logs to help debugging
   */
  // console.log("shouldFetchList with these args ", listArgs, "?");
  const list = findListFromArgs(state, listArgs);
  // console.log("LIST in question: ", list);
  if(!list || !list.items) {
    // yes, the list we're looking for wasn't found
    // console.log("X shouldFetch - true: list not found");
    return true;
  } else if(list.isFetching) {
    // no, this list is already fetching
    // console.log("X shouldFetch - false: fetching");
    return false
  } else if(new Date().getTime() - list.lastUpdated > (1000 * 60 * 5)) {
    // yes, it's been longer than 5 minutes since the last fetch
    // console.log("X shouldFetch - true: older than 5 minutes");
    return true;
  } else {
    // maybe, depends on if the list was invalidated
    // console.log("X shouldFetch - " + list.didInvalidate + ": didInvalidate");
    return list.didInvalidate;
  }
}

export const fetchListIfNeeded = (...listArgs) => (dispatch, getState) => {
  if(listArgs.length === 0) {
    // If no arguments passed, make the list we want "all"
    listArgs = ["all"];
  }
  if(shouldFetchList(getState(), listArgs)) {
    return dispatch(fetchList(...listArgs));
  } else {
    return dispatch(returnSiteLocationListPromise(...listArgs));
  }
}

export const returnSiteLocationListPromise = (...listArgs) => (dispatch, getState) => {
  /**
   * This returns the list object from the reducer so that we can do things with it in
   * the component.
   *
   * For the "fetchIfNeeded()" functionality, we need to return a promised object
   * EVEN IF we don't need to fetch it. This is because if we have any .then()'s
   * in the components, they will fail when we don't need to fetch.
   */

  // return the array of objects just like the regular fetch
  const state = getState();
  const listItemIds = findListFromArgs(state, listArgs).items
  const listItems = listItemIds.map(id => state.siteLocation.byId[id]);

  return new Promise((resolve) => {
    resolve({
      list: listItems
      , listArgs: listArgs
      , success: true
      , type: "RETURN_SITE_LOCATION_LIST_WITHOUT_FETCHING"
    })
  });
}

export const REQUEST_SITE_LOCATION_LIST = "REQUEST_SITE_LOCATION_LIST"
function requestSiteLocationList(listArgs) {
  return {
    type: REQUEST_SITE_LOCATION_LIST
    , listArgs
  }
}

export const RECEIVE_SITE_LOCATION_LIST = "RECEIVE_SITE_LOCATION_LIST"
function receiveSiteLocationList(json, listArgs) {
  return {
    type: RECEIVE_SITE_LOCATION_LIST
    , listArgs
    , list: json.siteLocations
    , success: json.success
    , error: json.message
    , receivedAt: Date.now()
  }
}

export const ADD_SITE_LOCATION_TO_LIST = "ADD_SITE_LOCATION_TO_LIST";
export function addSiteLocationToList(id, ...listArgs) {
  // console.log("Add siteLocation to list", id);
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: ADD_SITE_LOCATION_TO_LIST
    , id
    , listArgs
  }
}

export const REMOVE_SITE_LOCATION_FROM_LIST = "REMOVE_SITE_LOCATION_FROM_LIST"
export function removeSiteLocationFromList(id, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ['all'];
  }
  return {
    type: REMOVE_SITE_LOCATION_FROM_LIST
    , id
    , listArgs
  }
}

export function fetchList(...listArgs) {
  return dispatch => {
    if(listArgs.length === 0) {
      // default to "all" list if we don't pass any listArgs
      listArgs = ["all"];
    }
    dispatch(requestSiteLocationList(listArgs))
    /**
     * determine what api route we want to hit
     *
     * NOTE: use listArgs to determine what api call to make.
     * if listArgs[0] == null or "all", return list
     *
     * if listArgs has 1 arg, return "/api/site-locations/by-[ARG]"
     *
     * if 2 args, additional checks required.
     *  if 2nd arg is a string, return "/api/site-locations/by-[ARG1]/[ARG2]".
     *    ex: /api/site-locations/by-category/:category
     *  if 2nd arg is an array, though, return "/api/site-locations/by-[ARG1]-list" with additional query string
     *
     * TODO:  make this accept arbitrary number of args. Right now if more
     * than 2, it requires custom checks on server
     */
    let apiTarget = "/api/site-locations";
    if(listArgs.length == 1 && listArgs[0] !== "all") {
      apiTarget += `/by-${listArgs[0]}`;
    } else if(listArgs.length == 2 && Array.isArray(listArgs[1])) {
      // length == 2 has it's own check, specifically if the second param is an array
      // if so, then we need to call the "listByValues" api method instead of the regular "listByRef" call
      // this can be used for querying for a list of siteLocations given an array of siteLocation id's, among other things
      apiTarget += `/by-${listArgs[0]}-list?`;
      // build query string
      for(let i = 0; i < listArgs[1].length; i++) {
        apiTarget += `${listArgs[0]}=${listArgs[1][i]}&`
      }
    } else if(listArgs.length == 2) {
      // ex: ("author","12345")
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
    } else if(listArgs.length > 2) {
      apiTarget += `/by-${listArgs[0]}/${listArgs[1]}`;
      for(let i = 2; i < listArgs.length; i++) {
        apiTarget += `/${listArgs[i]}`;
      }
    }
    return callAPI(apiTarget).then(
      json => dispatch(receiveSiteLocationList(json, listArgs))
    )
  }
}

/**
 * LIST UTIL METHODS
 */
export const SET_SITE_LOCATION_FILTER = "SET_SITE_LOCATION_FILTER"
export function setFilter(filter, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_SITE_LOCATION_FILTER
    , filter
    , listArgs
  }
}

export const SET_SITE_LOCATION_PAGINATION = "SET_SITE_LOCATION_PAGINATION"
export function setPagination(pagination, ...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: SET_SITE_LOCATION_PAGINATION
    , pagination
    , listArgs
  }
}

export const INVALIDATE_SITE_LOCATION_LIST = "INVALIDATE_SITE_LOCATION_LIST"
export function invalidateList(...listArgs) {
  if(listArgs.length === 0) {
    listArgs = ["all"];
  }
  return {
    type: INVALIDATE_SITE_LOCATION_LIST
    , listArgs
  }
}



// WEBPACK FOOTER //
// modules/siteLocation/siteLocationActions.js